import { AnimatePresence, motion } from 'framer-motion';
import { Building2, CreditCard, Headphones, TrendingUp, UserCheck, UserPlus } from 'lucide-react';

import { Icon as SharedIcon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminPermissions } from '../../../hooks/useAdminPermissions';

interface Dashboard {
  id: string;
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  isLocked?: boolean;
}

const DASHBOARDS: Dashboard[] = [
  {
    id: 'sales',
    name: 'Sales Dashboard',
    path: '/analytics/sales',
    icon: TrendingUp,
    color: 'text-blue-600',
  },
  {
    id: 'support',
    name: 'Support Dashboard',
    path: '/analytics/support',
    icon: Headphones,
    color: 'text-emerald-600',
  },
  {
    id: 'stripe',
    name: 'Stripe Dashboard',
    path: '/analytics/stripe',
    icon: CreditCard,
    color: 'text-purple-600',
  },
  {
    id: 'onboarding',
    name: 'Onboarding Dashboard',
    path: '/analytics/onboarding',
    icon: UserPlus,
    color: 'text-orange-600',
  },
  {
    id: 'subscriber-health',
    name: 'Subscriber Health Dashboard',
    path: '/analytics/subscriber-demographics',
    icon: UserCheck,
    color: 'text-cyan-600',
  },
  {
    id: 'orgs1',
    name: 'ORGS1 Dashboard',
    path: '/analytics/orgs1',
    icon: Building2,
    color: 'text-indigo-600',
  },
];

interface DashboardNavigationProps {
  currentDashboardId: string;
}

const DashboardNavigation = ({ currentDashboardId }: DashboardNavigationProps) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { hasPermission } = useAdminPermissions();

  const dashboards = DASHBOARDS.filter(
    dashboard => dashboard.id !== 'stripe' || hasPermission('analytics.stripe.view')
  );
  const currentDashboard = dashboards.find(d => d.id === currentDashboardId);
  const otherDashboards = dashboards.filter(d => d.id !== currentDashboardId);

  if (!currentDashboard) return null;

  const CurrentIcon = currentDashboard.icon;

  return (
    <div className="bg-gradient-to-r from-white via-gray-50/50 to-white dark:from-gray-800 dark:via-gray-800/80 dark:to-gray-800 border-b border-gray-200/60 dark:border-gray-700/60">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left: Modern Breadcrumb Navigation */}
          <div className="flex items-center gap-3">
            {/* Back to Hub Button - Enhanced */}
            <motion.button
              whileHover={{ scale: 1.02, x: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/analytics')}
              className="group flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 bg-white dark:bg-gray-800 hover:bg-gradient-to-br hover:from-gray-50 hover:to-white dark:hover:from-gray-700 dark:hover:to-gray-800 rounded-xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-md transition-all duration-300 h-[42px]"
            >
              <SharedIcon
                icon={Icons.chevronLeft}
                className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform duration-300"
              />
              <span>Analytics Hub</span>
            </motion.button>

            {/* Separator - Modern Design */}
            <div className="flex items-center">
              <svg
                className="h-5 w-5 text-gray-300 dark:text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>

            {/* Current Dashboard Dropdown - Enhanced */}
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="group flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold bg-white dark:bg-gray-800 hover:bg-gradient-to-br hover:from-gray-50 hover:to-white dark:hover:from-gray-700 dark:hover:to-gray-800 rounded-xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm hover:shadow-md transition-all duration-300 h-[42px]"
              >
                <div
                  className={`p-1.5 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 group-hover:scale-110 transition-transform duration-300`}
                >
                  <CurrentIcon className={`h-4 w-4 ${currentDashboard.color}`} />
                </div>
                <span className="text-gray-900 dark:text-gray-100">{currentDashboard.name}</span>
                <SharedIcon
                  icon={Icons.chevronDown}
                  className={`h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-all duration-300 ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </motion.button>

              {/* Dropdown Menu - Enhanced */}
              <AnimatePresence>
                {isDropdownOpen && (
                  <>
                    {/* Backdrop to close dropdown */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-40"
                      onClick={() => setIsDropdownOpen(false)}
                    />

                    {/* Dropdown Content */}
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      className="absolute left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200/60 dark:border-gray-700/60 py-1.5 z-50 backdrop-blur-xl"
                    >
                      <div className="px-3 py-2">
                        <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <svg
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7h12M8 12h12M8 17h12M4 7h.01M4 12h.01M4 17h.01"
                            />
                          </svg>
                          Switch Dashboard
                        </div>
                      </div>
                      <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                      <div className="px-1.5 py-1 space-y-0.5">
                        {otherDashboards.map(dashboard => {
                          const Icon = dashboard.icon;
                          const isLocked = dashboard.isLocked;

                          return (
                            <motion.button
                              key={dashboard.id}
                              whileHover={isLocked ? {} : { scale: 1.01, x: 2 }}
                              whileTap={isLocked ? {} : { scale: 0.98 }}
                              onClick={() => {
                                if (!isLocked) {
                                  navigate(dashboard.path);
                                  setIsDropdownOpen(false);
                                }
                              }}
                              disabled={isLocked}
                              className={`w-full flex items-center gap-2.5 px-2.5 py-2 text-sm rounded-lg transition-all duration-200 group ${
                                isLocked
                                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60'
                                  : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gradient-to-r hover:from-blue-50 hover:via-indigo-50 hover:to-purple-50 dark:hover:from-blue-900/30 dark:hover:via-indigo-900/30 dark:hover:to-purple-900/30'
                              }`}
                            >
                              <div
                                className={`p-1.5 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 ${
                                  isLocked ? '' : 'group-hover:scale-105'
                                } transition-transform duration-200`}
                              >
                                <Icon className={`h-3.5 w-3.5 ${dashboard.color}`} />
                              </div>
                              <span className="font-medium flex-1 text-left">{dashboard.name}</span>
                              {isLocked ? (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-500">
                                    Soon
                                  </span>
                                  <SharedIcon
                                    icon={Icons.lock}
                                    className="h-3 w-3 text-amber-500 dark:text-amber-600"
                                  />
                                </div>
                              ) : (
                                <SharedIcon
                                  icon={Icons.chevronLeft}
                                  className="h-3.5 w-3.5 rotate-180 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                />
                              )}
                            </motion.button>
                          );
                        })}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Right: Live Data Indicator - Enhanced */}
          <div className="flex items-center gap-3">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="hidden sm:flex items-center gap-2.5 px-3 py-2 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200/50 dark:border-green-700/50"
            >
              <div className="relative">
                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                <div className="absolute inset-0 h-2 w-2 bg-green-400 rounded-full animate-ping" />
              </div>
              <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                Live Data
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardNavigation;
